import React, { useState, useRef, useEffect } from 'react';
import { Button, Textarea } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useMention } from '~/components/contexts/MentionContext';
import MentionSuggestion from '~/components/molecules/MentionSuggestion/MentionSuggestion';
import useUserContext from '~/hooks/useUserContext';
import { useAuthModal } from '~/hooks';

interface CommentInputProps {
  onSubmit: (text: string) => void;
  loading?: boolean;
  providerX?: {
    top?: string;
    bottom?: string;
  };
  handleAuth?: () => void;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  onSubmit,
  loading = false,
  providerX = {
    bottom: '96px',
  },
  handleAuth,
}) => {
  const [text, setText] = useState('');
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLFormElement>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const { addMention, formatTextWithMentions } = useMention();
  const [isMentioning, setIsMentioning] = useState(false);
  const { user } = useUserContext();
  const { requireAuth } = useAuthModal();

  const handleSubmit = (e: React.FormEvent) => {

    if(!user) {
      e.preventDefault();
      handleAuth?.();
      requireAuth(() => undefined);
      return;
    }

    e.preventDefault();
    if (text.trim()) {
      const formattedText = formatTextWithMentions(text);
      onSubmit(formattedText);
      setText('');
      setIsMentioning(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    
    const cursorPosition = e.target.selectionStart;
    checkForMention(newText, cursorPosition);
  };

  const checkForMention = (content: string, cursorPosition: number | null) => {
    if (cursorPosition === null || !textareaRef.current) return;
    
    const textBeforeCursor = content.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex === -1) {
      setIsMentioning(false);
      return;
    }
    
    // Check if there's a space or newline between @ and cursor
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
    if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
      setIsMentioning(false);
      return;
    }
    
    // Extract query
    const query = textAfterAt.trim();
    setMentionQuery(query);
    setIsMentioning(true);
  
  };

  const handleSelectMention = (user: { id: string; username: string }) => {
    if (!textareaRef.current) return;
    
    addMention(user);
    
    const cursorPosition = textareaRef.current.selectionStart;
    if (cursorPosition === null) return;
    
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex === -1) return;
    
    const newText = 
      text.substring(0, lastAtIndex) + 
      `@${user.username} ` + 
      text.substring(cursorPosition);
    
    setText(newText);
    setIsMentioning(false);
    
    // Focus and move cursor after mention
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPosition = lastAtIndex + user.username.length + 2;
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  const closeMentionSuggestion = () => {
    setIsMentioning(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeMentionSuggestion();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <form ref={containerRef} onSubmit={handleSubmit} className="w-full relative">
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Textarea
            ref={textareaRef}
            placeholder={t('comments.writeComment') || ''}
            value={text}
            onChange={handleTextChange as any}
            className="w-full min-h-24"
            minRows={2}
            maxRows={2}
            maxLength={180}
            size="sm"
            radius="sm"
            variant="bordered"
          />
          {isMentioning && (
            <div 
              className="absolute left-0 right-0 max-h-[250px] overflow-hidden shadow-lg z-10"
              style={{ 
                ...providerX,
              }}
            >
              <MentionSuggestion
                query={mentionQuery}
                onSelect={handleSelectMention}
                onClose={closeMentionSuggestion}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            color="warning"
            size="sm"
            radius="sm"
            isDisabled={!text.trim() || loading}
            isLoading={loading}
            className="px-4 sm:px-6"
          >
            {t('comments.submit')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CommentInput; 